/* eslint-disable @typescript-eslint/no-unused-vars */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ClusterModel } from '@app/core/models';
import { APP_CONFIG, IAppConfig, StorageService } from '@app/core/services';
import { ClusterApiService } from '../services/api';

const TSTATE_KEY = makeStateKey<ClusterModel>('cluster-resolver');

@Injectable()
export class ClusterResolver implements Resolve<ClusterModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: IAppConfig,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly clusterService: ClusterApiService,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly transferState: TransferState,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<ClusterModel> | Promise<ClusterModel> | ClusterModel {
    const slug = route.paramMap.get('clusterSlug');

    return this.isServer ? this.serverResolve(slug) : this.browserResolve(slug);
  }

  private browserResolve(
    slug: string,
  ): Observable<ClusterModel> | ClusterModel {
    const tmp = this.transferState.get<Pick<ClusterModel, keyof ClusterModel>>(
      TSTATE_KEY,
      null,
    );

    if (tmp != null) {
      this.transferState.remove<ClusterModel>(TSTATE_KEY);
      return ClusterModel.copyObj(tmp);
    }

    const clusters: Array<ClusterModel> = this.storageService.CLUSTERS;
    if (clusters) {
      const cluster = clusters.find(el => el.slug === slug);
      if (cluster != null) {
        return cluster;
      }
    }
    if (slug) {
      return this.clusterService.getCluster(slug);
    }

    const cluster = this.storageService.SELECTED_CLUSTER;
    if (cluster?.id) {
      return cluster;
    }
    if (!this.appConfig.settings.cluster) {
      console.error('No cluster in settings', this.appConfig.settings);
      return null;
    }
    return this.clusterService.getClusterInfo(
      this.appConfig.settings.cluster,
      false,
    );
  }

  private serverResolve(slug: string): Observable<ClusterModel> {
    if (!slug && !this.appConfig.settings.cluster) {
      console.error('No cluster in settings', this.appConfig.settings);
      return null;
    }
    const result = slug
      ? this.clusterService.getCluster(slug)
      : this.clusterService.getClusterInfo(
          this.appConfig.settings.cluster,
          false,
        );

    return result.pipe(
      tap(data => {
        this.transferState.set<ClusterModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }
}
